import React, {useEffect, useState} from 'react';
import {useAuthState} from '../../../shared/services/firebase';
import {useTenants} from '../context/TenantsContext';
import {Button, Spinner} from 'react-bootstrap';
import {useHistory} from 'react-router-dom';
import ArrowBack from "@mui/icons-material/ArrowBackRounded";
import SubTenantsSection from './SubTenantsSection';
import EdgesSection from './EdgesSection';
import MembersSection from './MembersSection';
import PropTypes from "prop-types";
import {tenantsApi} from '../services/tenantsApi';

const TenantDetail = ({path}) => {
  const {user} = useAuthState();
  const {currentTenant} = useTenants();
  const history = useHistory();
  const [tenant, setTenant] = useState(null);
  const [subTenants, setSubTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError("");
    setIsOwner(false);

    const unsubTenant = tenantsApi.subscribeTenant(path, {
      onNext: (tenantData) => {
        setTenant(tenantData);

        const parentIds = tenantData.parentTenantIds || [];
        setIsOwner(currentTenant?.role === 'owners' && parentIds.includes(currentTenant?.id));
        setLoading(false);
      },
      onError: (err) => {
        console.error('Error fetching tenant:', err);
        setError(err.code === 'TENANT_NOT_FOUND'
          ? 'Tenant not found'
          : 'Error fetching tenant details. You might not have permission.');
        setLoading(false);
      }
    });

    const unsubSubTenants = tenantsApi.subscribeSubTenants(path, currentTenant?.id, {
      onNext: (subs) => {
        setSubTenants(subs);
      },
      onError: (err) => {
        console.error('Error fetching sub-tenants:', err);
      }
    });

    return () => {
      unsubTenant();
      unsubSubTenants();
    };
  }, [path, user, currentTenant]);

  const navigateToSubTenant = (subId) => {
    const newPath = path.endsWith('/') ? `${path}tenants/${subId}` : `${path}/tenants/${subId}`;
    history.push(`/tenants?path=${newPath}`);
  };

  const goBack = () => {
    const parts = path.split('/tenants/');
    if (parts.length > 1) {
      parts.pop();
      const parentPath = parts.join('/tenants/');
      history.push(parentPath === 'tenants' ? '/tenants' : `/tenants?path=${parentPath}`);
    } else {
      history.push('/tenants');
    }
  };

  if (loading) return <div className="text-center mt-5"><Spinner animation="border"/></div>;
  if (error) return <div className="text-center mt-5 text-danger"><h3>{error}</h3><Button variant="outline-primary"
                                                                                          onClick={() => history.push('/tenants')}>Back
    to List</Button></div>;
  if (!tenant) return null;

  const canGoBack = currentTenant?.path && path !== currentTenant.path;

  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        {canGoBack && (
          <Button variant="link" className="p-0 me-3" onClick={goBack}>
            <ArrowBack fontSize="large"/>
          </Button>
        )}
        <h2 className="mb-0">{tenant.name} <small className="text-muted h6">({tenant.id})</small></h2>
      </div>

      <hr/>

      <SubTenantsSection
        subTenants={subTenants}
        tenant={tenant}
        path={path}
        isOwner={isOwner}
        user={user}
        onNavigate={navigateToSubTenant}
      />

      <EdgesSection
        tenant={tenant}
        path={path}
        subTenants={subTenants}
        isOwner={isOwner}
      />

      <MembersSection
        tenant={tenant}
        path={path}
        isOwner={isOwner}
      />
    </div>
  );
};

TenantDetail.propTypes = {
  path: PropTypes.string.isRequired
};

export default TenantDetail;
